<div class="form-panel">
  <div class="form-panel__header">
    <h2>{{ formTitleKey | translate }}</h2>
    <button type="button" class="form-panel__close" (click)="cancel()">×</button>
  </div>

  <div class="form-panel__body">
    <!-- Name -->
    <div class="form-group">
      <label class="form-label">{{ 'cmp.form.name' | translate }} <span class="required">*</span></label>
      <input type="text" class="form-input" [(ngModel)]="name" [placeholder]="'cmp.form.namePlaceholder' | translate">
    </div>

    <!-- Owner entity -->
    <div class="form-group">
      <label class="form-label">{{ 'cmp.form.publisherId' | translate }} <span class="required">*</span></label>
      <select class="form-select"
              [ngModel]="publisherId"
              (ngModelChange)="onEntityChange($event)"
              [disabled]="isEntityLocked">
        <option value="" disabled>{{ 'cmp.form.publisherIdPlaceholder' | translate }}</option>
        <option *ngFor="let entity of entityOptions" [value]="entity.value">{{ entity.label }}</option>
      </select>
    </div>

    <!-- Language + Country -->
    <div class="form-row-2">
      <div class="form-group">
        <label class="form-label">{{ 'cmp.form.consentLanguage' | translate }}</label>
        <select class="form-select" [(ngModel)]="consentLanguage" (change)="onConsentLanguageChange()">
          <option *ngFor="let lang of availableLanguages" [value]="lang">{{ lang }}</option>
        </select>
      </div>
      <div class="form-group">
        <label class="form-label">{{ 'cmp.form.publisherCC' | translate }}</label>
        <select class="form-select" [(ngModel)]="publisherCC">
          <option *ngFor="let lang of availableLanguages" [value]="lang">{{ lang }}</option>
        </select>
      </div>
    </div>

    <!-- CMP ID + Core Version -->
    <div class="form-row-2">
      <div class="form-group">
        <label class="form-label">{{ 'cmp.form.cmpId' | translate }} <span class="required">*</span></label>
        <input type="number" class="form-input" [(ngModel)]="cmpId" min="1">
      </div>
      <div class="form-group">
        <label class="form-label">{{ 'cmp.form.coreVersion' | translate }}</label>
        <select class="form-select" [(ngModel)]="configCoreVersion">
          <option *ngFor="let v of availableCoreVersions" [value]="v">{{ v }}</option>
        </select>
        <p class="form-hint core-version-warning" *ngIf="isCoreOutdated">
          ⚠ {{ 'cmp.form.coreVersionOutdated' | translate: { current: latestCoreVersion } }}
        </p>
        <p class="form-hint core-version-warning" *ngIf="isMajorBump">
          {{ 'cmp.form.coreVersionMajorBump' | translate: { major: currentCoreMajor } }}
        </p>
      </div>
    </div>

    <!-- GVL Version -->
    <div class="form-group">
      <label class="form-label">
        {{ 'cmp.form.vendorListVersion' | translate }}
        <span class="form-hint" *ngIf="gvlLoading"> ({{ 'common.loading' | translate }})</span>
        <span class="form-hint error" *ngIf="gvlError"> ({{ gvlError }})</span>
      </label>
      <input type="number" class="form-input" [(ngModel)]="vendorListVersion" min="1">
    </div>

    <!-- Privacy Policy URL -->
    <div class="form-group">
      <label class="form-label">{{ 'cmp.form.privacyPolicyUrl' | translate }}</label>
      <input type="text" class="form-input" [(ngModel)]="privacyPolicyUrl" placeholder="https://example.com/privacy">
    </div>

    <!-- Auto Show -->
    <div class="form-group">
      <label class="form-label">{{ 'cmp.form.autoShow' | translate }}</label>
      <div class="toggle-row">
        <label class="toggle-control">
          <input type="checkbox" [(ngModel)]="autoShow">
          <span class="toggle-slider"></span>
        </label>
        <span class="toggle-label">{{ autoShow ? ('cmp.form.autoShowOn' | translate) : ('cmp.form.autoShowOff' | translate) }}</span>
      </div>
    </div>

    <!-- Purposes -->
    <div class="form-group">
      <label class="form-label">{{ 'cmp.form.purposes' | translate }}</label>
      <div class="purposes-list">
        <div class="purpose-row" *ngFor="let p of availablePurposes">
          <label class="checkbox-label" [class.required-purpose]="p.required">
            <input type="checkbox"
                   [checked]="isPurposeSelected(p.id)"
                   [disabled]="p.required"
                   (change)="togglePurpose(p.id)">
            <span class="purpose-id" [title]="('cmp.form.purposeLabel' | translate) + ' ' + p.id">P{{ p.id }}</span>
            <span class="purpose-name">{{ p.name }}</span>
          </label>
          <span class="purpose-basis" [class.li]="p.legalBasis === 'legitimateInterest'"
                [title]="p.legalBasis === 'consent' ? ('cmp.form.legalBasisConsent' | translate) : ('cmp.form.legalBasisLI' | translate)">
            {{ p.legalBasis === 'consent' ? 'C' : 'LI' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Vendors from GVL -->
    <div class="form-group">
      <label class="form-label">
        {{ 'cmp.form.vendors' | translate }}
        <span class="selected-count" *ngIf="selectedVendors.length">({{ selectedVendors.length }} {{ 'cmp.form.vendorsSelected' | translate }})</span>
      </label>

      <!-- Selected vendors chips -->
      <ng-container *ngIf="selectedVendors.length">
        <div class="vendor-chips-all" *ngIf="selectedVendors.length === gvlVendors.length; else vendorChips">
          <span class="vendor-all-label">{{ 'cmp.form.allVendorsSelected' | translate }} ({{ selectedVendors.length }})</span>
          <button type="button" class="vendor-all-clear" (click)="selectedVendors = []">{{ 'cmp.form.deselectAll' | translate }}</button>
        </div>
        <ng-template #vendorChips>
          <div class="vendor-chips">
            <span class="vendor-chip" *ngFor="let v of selectedVendors">
              {{ v.name }}
              <button type="button" (click)="removeVendor(v.id)">×</button>
            </span>
          </div>
        </ng-template>
      </ng-container>

      <!-- GVL vendor picker -->
      <div class="vendor-picker" *ngIf="!gvlLoading && !gvlError">
        <input type="search"
               class="form-input vendor-search"
               [(ngModel)]="vendorSearch"
               [placeholder]="'cmp.form.vendorSearchPlaceholder' | translate">
        <div class="vendor-list-header">
          <button type="button" class="select-all-btn" (click)="toggleAllFilteredVendors()">
            {{ allFilteredSelected ? ('cmp.form.deselectAll' | translate) : ('cmp.form.selectAll' | translate) }}
          </button>
        </div>
        <div class="vendor-list">
          <div class="vendor-item"
               *ngFor="let v of filteredGvlVendors"
               [class.selected]="isVendorSelected(v)"
               (click)="toggleVendor(v)">
            <span class="vendor-id">#{{ v.id }}</span>
            <span class="vendor-name">{{ v.name }}</span>
            <span class="vendor-check" *ngIf="isVendorSelected(v)">✓</span>
          </div>
          <div class="vendor-empty" *ngIf="filteredGvlVendors.length === 0">
            {{ 'cmp.form.vendorNoResults' | translate }}
          </div>
        </div>
      </div>
      <p class="form-hint" *ngIf="gvlLoading">{{ 'common.loading' | translate }} GVL...</p>
      <p class="form-hint error" *ngIf="gvlError">{{ gvlError }}</p>
    </div>

    <!-- Status -->
    <div class="form-group">
      <label class="form-label">{{ 'cmp.form.status' | translate }}</label>
      <div class="toggle-row">
        <label class="toggle-control">
          <input type="checkbox" [checked]="status === 1" (change)="status = $any($event.target).checked ? 1 : 0">
          <span class="toggle-slider"></span>
        </label>
        <span class="toggle-label">
          {{ status === 1 ? ('cmp.fields.statusActive' | translate) : ('cmp.fields.statusInactive' | translate) }}
        </span>
      </div>
    </div>

    <!-- Providers -->
    <div class="form-group">
      <label class="form-label">{{ 'cmp.form.providers' | translate }}</label>

      <!-- Publisher toggle -->
      <div class="provider-item publisher-item"
           [class.selected]="publisher"
           [class.disabled]="isPublisherDisabled()"
           (click)="isPublisherDisabled() ? showProviderTooltip($event, publisherId) : onPublisherItemClick()">
        <label class="checkbox-label" style="pointer-events: none;">
          <input type="checkbox"
                 [(ngModel)]="publisher"
                 [disabled]="isPublisherDisabled()"
                 (change)="onPublisherToggle()">
          <span>{{ 'cmp.form.publisherProvider' | translate }}</span>
        </label>
        <span class="provider-id">{{ publisherId }}</span>
      </div>

      <div class="providers-list" *ngIf="providers.length; else noProviders">
        <div class="provider-item"
             *ngFor="let p of providers"
             [class.selected]="selectedProviderIds.includes(p.ID)"
             [class.disabled]="isProviderDisabled(p.ID)"
             (click)="toggleProvider(p.ID)"
             (mouseenter)="showProviderTooltip($event, p.ID)">
          <span class="provider-check">{{ selectedProviderIds.includes(p.ID) ? '✓' : '' }}</span>
          <span>{{ p.Name || p.ID }}</span>
          <span class="provider-id">{{ p.ID }}</span>
        </div>
      </div>
      <ng-template #noProviders>
        <p class="form-hint">{{ 'cmp.form.noProviders' | translate }}</p>
      </ng-template>
    </div>

    <!-- Advanced Settings -->
    <div class="form-group">
      <button type="button" class="advanced-toggle" (click)="showAdvanced = !showAdvanced">
        {{ 'cmp.form.advanced' | translate }}
        <span class="chevron" [class.open]="showAdvanced">▾</span>
      </button>
      <div class="advanced-content" *ngIf="showAdvanced">
        <!-- Cookie Name -->
        <div class="form-group">
          <label class="form-label">{{ 'cmp.form.cookieName' | translate }}</label>
          <input type="text" class="form-input" [(ngModel)]="cookieName">
        </div>
        <!-- Cookie Expire Days -->
        <div class="form-group">
          <label class="form-label">{{ 'cmp.form.cookieExpireDays' | translate }}</label>
          <input type="number" class="form-input" [(ngModel)]="cookieExpireDays" min="1" max="3650">
        </div>
        <!-- i18n overrides -->
        <div class="form-group">
          <div class="advanced-toggle-row">
            <label class="form-label">{{ 'cmp.form.i18n' | translate }}</label>
            <label class="toggle-control small">
              <input type="checkbox" [(ngModel)]="showI18n">
              <span class="toggle-slider"></span>
            </label>
          </div>
          <div *ngIf="showI18n">
            <textarea class="form-textarea code"
                      [(ngModel)]="i18nJson"
                      (blur)="validateI18n()"
                      rows="6"
                      placeholder='{"bannerTitle": "...", "acceptAll": "..."}'></textarea>
            <p class="form-hint error" *ngIf="i18nError">{{ i18nError }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- onConsentSaved callback -->
    <div class="form-group">
      <label class="form-label">{{ 'cmp.form.onConsentSaved' | translate }}</label>
      <app-js-editor
        [(ngModel)]="onConsentSavedScript"
        [wrapperFirst]="'function onConsentSaved(consentData) {'"
        [wrapperLast]="'}'">
      </app-js-editor>
    </div>
  </div>

  <div class="form-panel__footer">
    <button type="button" class="ghost-btn" (click)="cancel()" [disabled]="saving">
      {{ 'common.cancel' | translate }}
    </button>
    <button type="button" class="primary-btn" (click)="save()" [disabled]="!isFormValid || saving">
      {{ saving ? ('common.saving' | translate) : ('common.save' | translate) }}
    </button>
  </div>

  <!-- Tooltip -->
  <div class="floating-tooltip"
       *ngIf="tooltipConfig"
       [style.top.px]="tooltipConfig.top"
       [style.left.px]="tooltipConfig.left">
    {{ tooltipConfig.message }}
  </div>
</div>
